import type { SearchCriteria } from '@/entities/searchCriteria';
import { getSelectedFilters, isFilterModelSelected } from './filtersModel';
import { getDynamicFilterOptions } from './getFiltersGroups';
import { createEmptySearchCriteriaFilters } from './searchFilters';
import { SERP_FILTER_GROUPS } from '../config/filters';
import { SERP_FILTERS_RESET_TEXTS } from '../config/texts';
import { type FiltersModel, type SerpResultsDictionaries, FilterGroupId } from '../model/types';

export function getFiltersResetLabels(filtersModel: FiltersModel, dictionaries: SerpResultsDictionaries): string[] {
  const dynamicOptions = getDynamicFilterOptions(dictionaries);
  const selectedFilters = getSelectedFilters(filtersModel, createEmptySearchCriteriaFilters(), dynamicOptions);
  if (isFilterModelSelected(filtersModel) && !selectedFilters.length) {
    const result: string[] = [];
    if (filtersModel.operators.length) {
      result.push(SERP_FILTER_GROUPS.find((filter) => filter.id === FilterGroupId.Operators)?.title || '');
    }
    if (filtersModel.prepayments.length) {
      result.push(SERP_FILTER_GROUPS.find((filter) => filter.id === FilterGroupId.Prepayments)?.title || '');
    }
    return result.filter(Boolean);
  }
  return selectedFilters.map((filter) => filter.label);
}

function quoteLabel(label: string): string {
  const { labelOpenQuote, labelCloseQuote } = SERP_FILTERS_RESET_TEXTS;

  return `${labelOpenQuote}${label}${labelCloseQuote}`;
}

function joinLabels(labels: readonly string[]): string {
  const { labelsJoin, labelsLastJoin } = SERP_FILTERS_RESET_TEXTS;
  const quoted = labels.map((label) => quoteLabel(label));
  const last = quoted.pop() ?? '';
  if (quoted.length === 0) return last;

  return `${quoted.join(labelsJoin)}${labelsLastJoin}${last}`;
}

export function formatFiltersResetDescription(labels: readonly string[]): string {
  if (labels.length === 0) return '';

  const { descriptionPrefix, descriptionSuffix } = SERP_FILTERS_RESET_TEXTS;

  return `${descriptionPrefix}${joinLabels(labels)}${descriptionSuffix}`;
}

function getCountryId({ destination }: SearchCriteria): number | null {
  return destination && destination.kind !== 'none' ? destination.country.id : null;
}

export function isCountryChanged(previous: SearchCriteria | null, next: SearchCriteria): boolean {
  const previousCountryId = previous && getCountryId(previous);
  const nextCountryId = getCountryId(next);

  return previousCountryId !== null && nextCountryId !== null && previousCountryId !== nextCountryId;
}
